import { useAuth } from "hooks/auth";
import { InfiniteResponse } from "hooks/useManageInfiniteScroll";
import { querystring } from "models/Listing";
import useSWR, { SWRResponse } from "swr";
import useSWRInfinite from "swr/infinite";
import { ListingProps, ListResponse } from "types/listing";
import { StoreData, StoreFEModel } from "types/stores";
import { getEnvVariable } from "utilities/env";

export const useStores = () => {
  const { fetcher } = useAuth();

  const useStoreList = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<StoreData>> => {
    return useSWR(
      `${getEnvVariable("REACT_APP_API_URL")}/store${querystring(props)}`,
      fetcher,
    );
  };

  const useInfiniteStoreList = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<StoreData>>(
      (index) =>
        `${getEnvVariable(
          "REACT_APP_API_URL",
        )}/store${querystring({ ...props, page: index + 1 })}`,
      fetcher,
      { revalidateFirstPage: false },
    );
  };

  const useStoreData = (storeID: string): SWRResponse<StoreData> => {
    return useSWR(
      () =>
        storeID
          ? `${getEnvVariable("REACT_APP_API_URL")}/store/${storeID}`
          : null,
      fetcher,
    );
  };

  const useInitialStoreData = (
    storeID: string,
    isFetched: boolean,
  ): StoreData | null => {
    const { data } = useSWR(
      () =>
        isFetched
          ? null
          : storeID
            ? `${getEnvVariable("REACT_APP_API_URL")}/store/${storeID}`
            : `${getEnvVariable("REACT_APP_API_URL")}/store`,
      fetcher,
    );
    return data && data.items ? data.items[0] : data;
  };

  const useStoreDetails = (storeCodes: string[]) => {
    const { data: stores } = useSWR(
      { url: getEnvVariable("REACT_APP_API_URL"), stores: storeCodes },

      async ({ url, stores }) => {
        const allPosibleStores = await Promise.all(
          stores.map((storeCode: string) =>
            fetcher(`${url}/store?code=${storeCode}`),
          ),
        );
        const allStores: { name: string; code: string }[] = [];

        allPosibleStores.forEach(({ items }) => {
          allStores.push(...items);
        });

        return allStores;
      },
    );

    return stores || [];
  };

  const addStore = async (
    props: StoreFEModel,
  ): Promise<{ data: { storeId: string } }> => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/store`,
      {
        body: JSON.stringify({ store: props }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();

    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const editStore = async ({
    storeId,
    active,
    name,
  }: Pick<StoreData, "storeId" | "active" | "name">) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/store/${storeId}`,
      {
        body: JSON.stringify({ store: { active, name } }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  return {
    addStore,
    editStore,
    useStoreData,
    useStoreList,
    useStoreDetails,
    useInitialStoreData,
    useInfiniteStoreList,
  };
};
